import React, { useEffect, useMemo, useState } from 'react';
import { API_URL } from '../api';
import PageLoadingSkeleton from './ui/PageLoadingSkeleton';
import ErrorBlock from './ui/ErrorBlock';
import './TenantGate.css';

const STORAGE_KEY = 'kanplan-active-tenant-slug';
const STORAGE_ROLE_KEY = 'kanplan-active-tenant-role';
const STORAGE_NAME_KEY = 'kanplan-active-tenant-name';

function getActiveTenantSlug() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v && String(v).trim() ? String(v).trim() : '';
  } catch {
    return '';
  }
}

function setActiveTenantSlug(slug) {
  try {
    localStorage.setItem(STORAGE_KEY, String(slug || '').trim());
  } catch {}
}

function setActiveTenantRole(role) {
  try {
    localStorage.setItem(STORAGE_ROLE_KEY, String(role || '').trim());
  } catch {}
}

function setActiveTenantName(name) {
  try {
    localStorage.setItem(STORAGE_NAME_KEY, String(name || '').trim());
  } catch {}
}

function clearActiveTenantSlug() {
  try { localStorage.removeItem(STORAGE_KEY); } catch {}
}

function clearActiveTenantRole() {
  try { localStorage.removeItem(STORAGE_ROLE_KEY); } catch {}
}

function clearActiveTenantName() {
  try { localStorage.removeItem(STORAGE_NAME_KEY); } catch {}
}

/* Rollen visas för användaren — råa slugar som "tenant_admin" hör inte hemma i UI. */
const ROLE_LABELS = {
  tenant_admin: 'Administratör',
  systemadmin: 'Systemadministratör',
  admin: 'Administratör',
  member: 'Medlem',
};

function roleLabel(role) {
  const key = String(role || '').trim();
  if (!key) return '';
  if (ROLE_LABELS[key]) return ROLE_LABELS[key];
  const pretty = key.replace(/_/g, ' ');
  return pretty.charAt(0).toUpperCase() + pretty.slice(1);
}

/* Initialer till organisationsmärket i listan — max två tecken. */
function tenantInitials(name) {
  const ord = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!ord.length) return '?';
  if (ord.length === 1) return ord[0].slice(0, 2).toUpperCase();
  return (ord[0][0] + ord[ord.length - 1][0]).toUpperCase();
}

/* KanPlan-märket. Samma form som inloggningsvyn och sidomenyn. */
function KanPlanBrand() {
  return (
    <div className="kp-tenant-gate__brand">
      <span className="kp-tenant-gate__brand-mark" aria-hidden="true">
        <svg viewBox="0 0 12 12" fill="none" width={17} height={17}>
          <path d="M2 5h3v5H2V5zm5-3h3v8H7V2z" fill="#fff" />
        </svg>
      </span>
      <span className="kp-tenant-gate__brand-name">KanPlan</span>
    </div>
  );
}

/**
 * branded — visar KanPlan-märket ovanför kortet. Standard är på, men de
 * fristående white-label-vyerna (StandaloneModuleShell) skickar false:
 * de ska inte visa KanPlan-branding för kundens användare.
 */
export function TenantGate({ children, branded = true }) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const activeSlug = useMemo(() => getActiveTenantSlug(), []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(`${API_URL}/tenants/mine`, { credentials: 'include' });
        if (!res.ok) {
          let msg = `HTTP ${res.status}`;
          try { const data = await res.json(); if (data?.detail) msg = data.detail; } catch {}
          throw new Error(msg);
        }
        const data = await res.json();
        const arr = Array.isArray(data?.items) ? data.items : [];
        if (!cancelled) setItems(arr);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Kunde inte hämta företag');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const validActive = useMemo(() => {
    if (!activeSlug) return false;
    return items.some(t => String(t?.slug) === String(activeSlug));
  }, [items, activeSlug]);

  useEffect(() => {
    if (!loading && activeSlug && !validActive && items.length > 0) {
      clearActiveTenantSlug();
      clearActiveTenantRole();
      clearActiveTenantName();
      try { window.location.reload(); } catch {}
    }
  }, [loading, activeSlug, validActive, items.length]);

  // If we have a valid active slug, ensure role is stored for nav gating.
  useEffect(() => {
    if (loading) return;
    if (!validActive) return;
    const match = items.find(t => String(t?.slug) === String(activeSlug));
    if (match && match.role) setActiveTenantRole(match.role);
    if (match) setActiveTenantName(match.legal_name || match.name || '');
  }, [loading, validActive, items, activeSlug]);

  if (loading) {
    return <PageLoadingSkeleton />;
  }

  if (error) {
    return <ErrorBlock message="Kunde inte ladda företag" detail={error} />;
  }

  if (!items.length) {
    return <ErrorBlock variant="empty" message="Inga företag kopplade" detail="Din användare har inga företag kopplade till sig ännu." />;
  }

  // Auto-select if user only has one tenant — skip the picker entirely
  if (!validActive && items.length === 1) {
    const only = items[0];
    setActiveTenantSlug(only.slug);
    setActiveTenantRole(only.role);
    setActiveTenantName(only.legal_name || only.name || '');
    try { window.location.reload(); } catch {}
    return <PageLoadingSkeleton />;
  }

  if (!validActive) {
    // Sökfältet är brus vid två-tre organisationer — visas först när listan
    // blir lång nog att man faktiskt behöver leta.
    const visaSok = items.length > 6;
    const filtered = items.filter(t => {
      const q = String(search || '').trim().toLowerCase();
      if (!q) return true;
      const hay = `${t?.name || ''} ${t?.slug || ''}`.toLowerCase();
      return hay.includes(q);
    });
    return (
      <div className="kp-tenant-gate">
        <div className="kp-tenant-gate__shell">
          {branded && <KanPlanBrand />}
          <div className="kp-tenant-gate__card">
          <div className="kp-tenant-gate__header">
            <h1 className="kp-tenant-gate__title">Välj organisation</h1>
            <p className="kp-tenant-gate__subtitle">
              Du är kopplad till flera organisationer. Välj vilken du vill arbeta i.
            </p>
          </div>

          {visaSok && (
          <div className="kp-tenant-gate__field">
            <label className="kp-tenant-gate__label" htmlFor="kp-tenant-search">Sök</label>
            <div className="kp-tenant-gate__input-wrap">
              <svg className="kp-tenant-gate__input-icon" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                id="kp-tenant-search"
                className="kp-tenant-gate__input"
                type="text"
                placeholder="Sök på namn"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
          </div>
          )}

          <div className="kp-tenant-gate__list">
            {filtered.map(t => (
              <button
                key={t.id || t.slug}
                type="button"
                className="kp-tenant-gate__item"
                onClick={() => {
                  setActiveTenantSlug(t.slug);
                  setActiveTenantRole(t.role);
                  setActiveTenantName(t.legal_name || t.name || '');
                  try { window.location.reload(); } catch {}
                }}
              >
                <span className="kp-tenant-gate__item-badge" aria-hidden="true">
                  {tenantInitials(t.name)}
                </span>
                <span className="kp-tenant-gate__item-text">
                  <span className="kp-tenant-gate__item-name">{t.name}</span>
                  {t.role && <span className="kp-tenant-gate__item-meta">{roleLabel(t.role)}</span>}
                </span>
                <svg className="kp-tenant-gate__item-chevron" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            ))}
            {!filtered.length && (
              <div className="kp-tenant-gate__empty">Ingen organisation matchar sökningen.</div>
            )}
          </div>
          </div>
        </div>
      </div>
    );
  }

  return children;
}

export function resetTenantSelection() {
  clearActiveTenantSlug();
  clearActiveTenantRole();
  try { window.location.reload(); } catch {}
}
